import {
  Menu,
  IconButton,
  MenuButton,
  MenuList,
  Button,
  Divider,
  Text,
  Flex,
  useMediaQuery,
  Input,
  Box,
} from '@chakra-ui/react';
import { useSelector, useDispatch } from 'react-redux';
import { useState } from 'react';

import AddIconComponent from 'components/Icons/AddIcon';
import ArrowDropdownIconComponent from 'components/Icons/ArrowDropdownIconComponent';
import PagesIcon from 'components/Icons/PagesIcon';
import { AppDispatch } from 'utils/GlobalHelpers';
import {
  prepareCreatePayload,
  getDashboard,
  setDashboard,
  setTimeFilter,
  getIsPageBarOpen,
  toggleIsPageBarOpen,
  updatePageOrder,
} from 'redux/DashboardSliceV2';

import ExportButtonNew from './Export/ExportButtonNew';
import TimeRangeFilter from './TimeRangeFilter';
import SelectScenario from './SelectScenario';
import InstrumentMenuList from './InstrumentMenuList';

const Header = () => {
  const dispatch: AppDispatch = useDispatch();
  const isPageBarOpen = useSelector(getIsPageBarOpen);
  const [smallDevice] = useMediaQuery('(max-width: 1100px)');
  const isSmallDevice = (smallDevice && isPageBarOpen) || smallDevice;
  const [isTablet] = useMediaQuery('(max-width: 768px)');
  const dashboard = useSelector(getDashboard);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');

  const handleNameDoubleClick = () => {
    setEditedName(dashboard?.name || '');
    setIsEditingName(true);
  };

  const handleNameSave = async () => {
    if (dashboard && editedName.trim()) {
      try {
        const updatedDashboard = {
          ...dashboard,
          name: editedName.trim(),
        };
        await dispatch(updatePageOrder({ dashboard: updatedDashboard })).then(() => {
          dispatch(setDashboard(updatedDashboard));
        });
      } catch {
        // Handle error if needed
      }
    }
    setIsEditingName(false);
  };

  const handleNameKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleNameSave();
    } else if (event.key === 'Escape') {
      setIsEditingName(false);
    }
  };

  const onSelectInstrument = (widgetType: string) => dispatch(prepareCreatePayload(widgetType));
  const rangeSelected = (start: string, end: string) => {
    if (dashboard?.id) {
      dispatch(setTimeFilter({ start, end }));
    }
  };

  const onPageToggle = () => {
    dispatch(toggleIsPageBarOpen(dashboard.model_id));
  };

  return (
    <>
      <Flex
        maxW={'100vw'}
        justifyContent={'space-between'}
        paddingBottom={'1rem'}
        borderBottom={'2px solid #FFFFFF15'}
        mx={isTablet ? '-2.5rem' : '-2rem'}
        px={isTablet ? '2.5rem' : '2rem'}
      >
        <Flex>
          {isEditingName ? (
            <Input
              value={editedName}
              onChange={(event) => setEditedName(event.target.value)}
              onBlur={handleNameSave}
              onKeyDown={handleNameKeyPress}
              color='white'
              fontSize='2xl'
              fontWeight='bold'
              fontFamily='regular'
              bg='rgba(255, 255, 255, 0.1)'
              border='1px solid rgba(255, 255, 255, 0.3)'
              _focus={{
                borderColor: 'rgba(255, 255, 255, 0.5)',
                boxShadow: 'none',
              }}
              maxWidth='450px'
              width='fit-content'
              mr={10}
              autoFocus
              height='auto'
              minHeight='40px'
            />
          ) : (
            <Text
              alignContent='center'
              color='white'
              fontSize='2xl'
              whiteSpace='nowrap'
              overflow='hidden'
              fontWeight='semibold'
              maxWidth='450px'
              fontFamily='regular'
              textOverflow='ellipsis'
              mr={10}
              cursor='pointer'
              onDoubleClick={handleNameDoubleClick}
              _hover={{ opacity: 0.8 }}
              title='Double-click to edit dashboard name'
            >
              {dashboard?.name}
            </Text>
          )}
          <SelectScenario />
          <Box height='40px' display='flex' alignItems='center' marginLeft='-10px'>
            <Divider orientation='vertical' height={'10px'} border={'1px solid #9C9DBA'} />
          </Box>
          {dashboard && <TimeRangeFilter rangeSelected={rangeSelected} />}
        </Flex>
        <Flex gap={2}>
          {isSmallDevice ? (
            <IconButton
              aria-label='Pages'
              icon={<PagesIcon />}
              backgroundColor={isPageBarOpen ? '#332663' : '#5A58AB'}
              borderRadius='sm'
              height='40px'
              onClick={onPageToggle}
              width={'fit-content'}
              border='none'
              fontSize={'md'}
              fontWeight={'400'}
              title={'Pages'}
              padding={'.5rem 1rem'}
            />
          ) : (
            <Button
              backgroundColor={isPageBarOpen ? '#332663' : '#5A58AB'}
              borderRadius='sm'
              height='40px'
              onClick={onPageToggle}
              border='none'
              leftIcon={<PagesIcon />}
              fontSize={'md'}
              fontWeight={'400'}
              width={'fit-content'}
              title={'Pages'}
              padding={'.5rem 1rem'}
            >
              Pages
            </Button>
          )}
          {/* <ExportButton /> */}
          <ExportButtonNew />
          <Menu variant={'dashboarding'}>
            {({ isOpen }) => {
              return (
                <>
                  <MenuButton
                    backgroundColor={'#5A58AB'}
                    padding={'.5rem 1rem'}
                    borderRadius={'sm'}
                    as={Button}
                    title={'Create'}
                    border={'none'}
                    rightIcon={<ArrowDropdownIconComponent height={12} width={12} color='white' />}
                    leftIcon={
                      <AddIconComponent style={{}} color='#62B5BA' height={16} width={16} />
                    }
                    fontSize={'md'}
                    fontWeight={'400'}
                    width={'fit-content'}
                    height={'40px'}
                  >
                    {isSmallDevice ? '' : 'Create'}
                  </MenuButton>
                  {isOpen && (
                    <MenuList zIndex={18}>
                      <InstrumentMenuList
                        onSelectInstrument={onSelectInstrument}
                        isCreateButton={true}
                      />
                    </MenuList>
                  )}
                </>
              );
            }}
          </Menu>
        </Flex>
      </Flex>
    </>
  );
};

export default Header;
